<template>
  <div>
    <PaginatedTable
      ref="table"
      table="functional-locations"
      :table-class="FunctionalLocation"
      :filter-definitions="filterDefinitions"
      :headers="headers"
    >
      <template #right-actions>
        <v-btn
          aria-label="Import Functional Locations"
          color="secondary"
          class="mr-2"
          flat
          @click="importer.open()"
        >
          <v-icon>mdi-upload</v-icon>Import
        </v-btn>
      </template>

      <template #item.actions="{ item }">
        <v-btn
          aria-label="View Functional Location"
          title="View"
          icon="mdi-eye"
          flat
          density="compact"
          @click="dialog?.open(item.id)"
        />
      </template>
    </PaginatedTable>
    <FunctionalLocationImport ref="importer" @imported="table?.getData()" />
    <FunctionalLocationDialog ref="dialog" @change="table?.getData()" />
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { FunctionalLocation } from "@/classes";
import FunctionalLocationDialog from "@/components/functional-location/FunctionalLocationDialog.vue";
import PaginatedTable from "@/components/base/tables/PaginatedTable.vue";
import { FilterDefinition } from "@/types/filter";
import { DataTableHeader } from "@/types/table";
import FunctionalLocationImport from "@/components/functional-location/FunctionalLocationImport.vue";

const headers: DataTableHeader[] = [
  {
    title: "Sup. Func. Loc.",
    key: "superior_functional_location",
    sortable: true,
  },
  {
    title: "Func. Loc.",
    key: "functional_location",
    sortable: true,
  },
  {
    title: "Is Asset",
    key: "is_asset_text",
    sortable: true,
    sortkey: "is_asset",
  },
  {
    title: "Asset Class",
    key: "asset_class_name",
    sortable: true,
  },
  {
    title: "Func. Loc. Description",
    key: "functional_location_description",
    sortable: true,
  },
  {
    title: "Cost Center",
    key: "cost_center",
    sortable: true,
  },
  {
    title: "Main Work Center",
    key: "main_work_center",
    sortable: true,
  },
  {
    title: "Func. Loc. Category",
    key: "functional_location_category",
    sortable: true,
  },
  {
    title: "Action",
    key: "actions",
    sortable: false,
  },
];

const filterDefinitions: FilterDefinition[] = [
  {
    label: "Sup. Func. Loc.",
    value: "superior_functional_location",
    type: "string",
  },
  {
    label: "Func. Loc.",
    value: "functional_location",
    type: "string",
  },
  {
    label: "Is Asset",
    value: "is_asset",
    type: "boolean",
  },
  {
    label: "Asset Class",
    value: "asset_class_id",
    type: "reflist",
    reflist: "assetClasses",
    itemValue: "id",
    itemText: "name",
    multiple: true,
  },
  {
    label: "Func. Loc. Description",
    value: "functional_location_description",
    type: "string",
  },
  {
    label: "Main Work Center",
    value: "main_work_center",
    type: "string",
  },
  {
    label: "Cost Center",
    value: "cost_center",
    type: "string",
  },
  {
    label: "Func. Loc. Category",
    value: "functional_location_category",
    type: "string",
  },
];

const table = ref<InstanceType<typeof PaginatedTable> | null>(null);
const dialog = ref<InstanceType<typeof FunctionalLocationDialog> | null>(null);
const importer = ref<InstanceType<typeof FunctionalLocationImport> | null>(
  null,
);
</script>
